/**
 * Compliance & Certifications — Source of Truth (public gate)
 * ==========================================================
 *
 * RÈGLE ABSOLUE : aucune certification, assurance, agrément ou norme ISO
 * ne peut apparaître publiquement sans preuve documentaire vérifiée.
 *
 * Un document n'est publiable que si TOUTES ces conditions sont vraies :
 *  - `file` (pièce justificative présente dans public/documents/compliance/)
 *  - `verifiedAt` renseigné (vérification interne effectuée)
 *  - `isPublic === true`
 *  - non expiré (`expiryDate` absent ou dans le futur)
 *
 * Les documents non vérifiés sont conservés (jamais supprimés) dans
 * `COMPLIANCE_ARCHIVE` et administrés depuis Lovable Cloud
 * (table `compliance_documents`, accès administrateur uniquement).
 * Ils sont communiqués exclusivement dans le cadre des appels d'offres
 * de l'État et des procédures contractuelles — jamais sur le site public.
 *
 * Consommé par :
 *  - <CertificationsSection /> (UI sur /compliance)
 *  - documents-validator-plugin.ts (vérification d'existence au build)
 *  - company-profile-manifest-plugin.ts (manifest JSON)
 */
export type CertStatus = 'valid' | 'inProgress' | 'expired' | 'pending';

/** Statuts calculés automatiquement pour le registre documentaire. */
export type ComplianceDocStatus =
  | 'valid'
  | 'expiring_soon'
  | 'expired'
  | 'pending_verification';

export type CertType = 'legal' | 'sector' | 'insurance' | 'qhse';

export interface ComplianceItem {
  /** Translation key for the human label (e.g. "certs.rccm"). */
  labelKey: string;
  /** Literal value or translation key resolved by the UI. */
  value: string;
  /** True if the value is a code/ID rendered in monospace. */
  isCode?: boolean;
  /** PDF filename in `public/documents/compliance/` (= preuve). */
  file?: string;
  /** ISO date YYYY-MM-DD. */
  validUntil?: string;
  /** Explicit status override. */
  status?: CertStatus;
  /** Optional override; otherwise inferred from the parent group's id. */
  type?: CertType;
  /** Date de vérification interne (ISO). Obligatoire pour publier. */
  verifiedAt?: string;
  /** Personne/service ayant vérifié la pièce. */
  verifiedBy?: string;
  /** Autorisation explicite de publication. */
  isPublic?: boolean;
}

export interface ComplianceGroup {
  titleKey: string;
  id: CertType;
  items: ComplianceItem[];
}

export const COMPLIANCE_DOCS_DIR = '/documents/compliance';

/** Fenêtres d'alerte avant expiration (jours). */
export const EXPIRY_ALERT_THRESHOLDS = [60, 30, 7] as const;

/**
 * Archive interne — conservée, non publiée.
 * Statut « en attente de vérification » tant qu'aucune preuve officielle
 * n'est jointe et validée par la direction.
 */
export const COMPLIANCE_ARCHIVE: ComplianceGroup[] = [
  {
    id: 'sector',
    titleKey: 'certs.sector',
    items: [
      { labelKey: 'certs.classBtp', value: 'certs.classBtp.value', validUntil: '2026-12-31', status: 'pending', isPublic: false },
      { labelKey: 'certs.publicMarkets', value: 'certs.publicMarkets.value', validUntil: '2026-12-31', status: 'pending', isPublic: false },
      { labelKey: 'certs.environment', value: 'certs.environment.value', validUntil: '2026-06-30', status: 'pending', isPublic: false },
    ],
  },
  {
    id: 'insurance',
    titleKey: 'certs.insurance',
    items: [
      { labelKey: 'certs.civilLiability', value: 'certs.civilLiability.value', validUntil: '2026-12-31', status: 'pending', isPublic: false },
      { labelKey: 'certs.allRisks', value: 'certs.allRisks.value', status: 'pending', isPublic: false },
      { labelKey: 'certs.workers', value: 'certs.workers.value', validUntil: '2026-12-31', status: 'pending', isPublic: false },
    ],
  },
  {
    id: 'qhse',
    titleKey: 'certs.qhse',
    items: [
      { labelKey: 'certs.iso9001', value: 'certs.iso9001.value', status: 'pending', isPublic: false },
      { labelKey: 'certs.iso45001', value: 'certs.iso45001.value', status: 'pending', isPublic: false },
      { labelKey: 'certs.iso14001', value: 'certs.iso14001.value', status: 'pending', isPublic: false },
    ],
  },
];

/**
 * Un élément est publiable uniquement avec preuve jointe, vérification
 * interne, autorisation de publication et validité non expirée.
 */
export const isPubliclyPublishable = (
  item: ComplianceItem,
  now: Date = new Date(),
): boolean => {
  if (!item.file || !item.verifiedAt || item.isPublic !== true) return false;
  if (item.status === 'pending' || item.status === 'inProgress') return false;
  if (item.validUntil) {
    const expiry = new Date(item.validUntil);
    if (Number.isNaN(expiry.getTime()) || expiry.getTime() < now.getTime()) return false;
  }
  return true;
};

/** Groupes réellement exposés au public (vide tant qu'aucune preuve n'est publiée). */
export const COMPLIANCE_GROUPS: ComplianceGroup[] = COMPLIANCE_ARCHIVE
  .map((group) => ({ ...group, items: group.items.filter((i) => isPubliclyPublishable(i)) }))
  .filter((group) => group.items.length > 0);

/**
 * Statut calculé d'un document du registre interne.
 * Aligné sur `public.compliance_document_effective_status` côté base.
 */
export const computeDocStatus = (doc: {
  status?: string | null;
  expiry_date?: string | null;
  document_url?: string | null;
  verified_at?: string | null;
}, now: Date = new Date()): ComplianceDocStatus => {
  if (!doc.document_url || !doc.verified_at) return 'pending_verification';
  if (doc.status === 'pending_verification') return 'pending_verification';
  if (doc.expiry_date) {
    const expiry = new Date(doc.expiry_date);
    if (!Number.isNaN(expiry.getTime())) {
      const days = Math.floor((expiry.getTime() - now.getTime()) / 86_400_000);
      if (days < 0) return 'expired';
      if (days <= 60) return 'expiring_soon';
    }
  }
  return 'valid';
};

/** Nombre de jours restants avant expiration (null si pas de date). */
export const daysUntilExpiry = (
  expiry?: string | null,
  now: Date = new Date(),
): number | null => {
  if (!expiry) return null;
  const date = new Date(expiry);
  if (Number.isNaN(date.getTime())) return null;
  return Math.floor((date.getTime() - now.getTime()) / 86_400_000);
};

/**
 * Palier d'alerte atteint (60, 30 ou 7 jours) ou null.
 */
export const expiryAlertThreshold = (
  expiry?: string | null,
  now: Date = new Date(),
): number | null => {
  const days = daysUntilExpiry(expiry, now);
  if (days === null || days < 0) return null;
  for (const threshold of [...EXPIRY_ALERT_THRESHOLDS].sort((a, b) => a - b)) {
    if (days <= threshold) return threshold;
  }
  return null;
};

/**
 * Returns true when `value` looks like a translation key.
 */
export const isTranslationKey = (value: string): boolean =>
  /^[a-zA-Z0-9._-]+$/.test(value) && value.includes('.');
